import {
  AnalyticsEventName,
  EXTENSION_ID,
  MessageTypes,
} from '@dupe/common/constants'
import axios from 'axios'
import {
  type OptionsType,
  deleteCookie,
  getCookie,
  setCookie,
} from 'cookies-next'
import { User, getAuth, onAuthStateChanged } from 'firebase/auth'
import { useRouter } from 'next/router'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { isFirefox } from 'react-device-detect'
import { useAuthUser } from '~/src/hooks'
import useAnalytics from '~/src/hooks/useAnalytics'
import { useAuthWebToken } from '~/src/hooks/useAuthWebToken'
import useFetchUser from '~/src/hooks/useFetchUser'
import useGetTempUserId from '~/src/hooks/useGetTempUserId'
import { useMaybeRegister } from '~/src/hooks/useMaybeRegister'
import usePostMessageListener from '~/src/hooks/usePostMessageListener'
import { DUPE_AUTH_USER_ID_KEY, MAX_AGE_OF_COOKIE } from '~/src/lib/constants'
import app from '~/src/lib/firebase/firebaseApp'
import getUserToken from '~/src/lib/getUserToken'
import logger from '~/src/lib/logger'
import { useDupeInputStore } from '~/src/stores/dupeInputStore'
import { useOnboardingStore } from '~/src/stores/useOnboardingStore'
import { UpdateUserPayload } from '~/src/types'

const auth = getAuth(app)

interface UserAuthData {
  uid: string
  accessToken: string
  tokenResult: any
}

interface OpenModalProps {
  callback?: (props?: {
    authUser: User | null | undefined
    userData: UpdateUserPayload | null | undefined
  }) => void
  onClose?: () => void
  allowLoggedIn?: boolean
  source?: string
  modalTitle?: React.ReactNode
  modalSubtitle?: React.ReactNode
  modalAsset?: string
  skipPhoneLink?: boolean
  disableClose?: boolean
  fullScreen?: boolean
  blurOverlay?: boolean
  skipOnboarding?: boolean
}

interface UpdateUserDetailsProps {
  subscription: {
    plan: string
    billingPeriod?: string
    paymentStatus?: string
    liveMode?: boolean
  }
  stripeCustomerId?: string
  stripeSubscriptionId?: string
}

/**
 * Auth Callback Flow:
 *
 * There are three main scenarios for when the auth callback is executed:
 *
 * 1. User authenticates but closes modal before completing onboarding
 *    - Callback is executed when modal is closed
 *    - This allows the app to proceed with authenticated state even if onboarding is incomplete
 *
 * 2. User completes both auth and onboarding
 *    - Callback is executed via handleAuthSuccess
 *    - This is the "happy path" where all user data is available
 *
 * 3. User starts auth but cancels before completing
 *    - No callback is executed
 *    - Processing state is reset
 *
 * The callback receives both authUser and userData, allowing the app to:
 * - Know if the user is authenticated (authUser)
 * - Access additional user data if onboarding was completed (userData)
 */

interface AuthContextType {
  isOpen: boolean
  openAuthModal: (props?: OpenModalProps) => void
  closeAuthModal: () => void
  handleAuthSuccess: (close?: () => void) => void
  authSource: string | null
  modalTitle?: React.ReactNode
  modalSubtitle?: React.ReactNode
  modalAsset?: string | null
  userData: UpdateUserPayload | null | undefined
  loadingUserData: boolean
  refetchUserData: () => Promise<{ user: UpdateUserPayload } | undefined>
  updateUserDetails: (
    props: UpdateUserDetailsProps
  ) => Promise<{ status: boolean; userData: UpdateUserPayload }>
  extensionInstalled:
    | {
        installed: boolean
        version: string
      }
    | undefined
  skipPhoneLink?: boolean
  disableClose?: boolean
  fullScreen?: boolean
  blurOverlay?: boolean
  skipOnboarding?: boolean
}

interface AuthProviderProps {
  children: React.ReactNode
}

const defaultAuthContext: AuthContextType = {
  isOpen: false,
  openAuthModal: () => {},
  closeAuthModal: () => {},
  handleAuthSuccess: () => {},
  authSource: null,
  userData: null,
  loadingUserData: false,
  refetchUserData: async () => undefined,
  updateUserDetails: async () => ({
    status: false,
    userData: {} as UpdateUserPayload,
  }),
  extensionInstalled: undefined,
  skipPhoneLink: false,
  disableClose: false,
  fullScreen: true,
  blurOverlay: false,
  skipOnboarding: false,
}

const AuthContext = createContext<AuthContextType>(defaultAuthContext)

export const useAuthContext = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const analytics = useAnalytics()
  const {
    authUser,
    loading: loadingAuthUser,
    authUserNotAnonymous,
  } = useAuthUser()
  const { tempUserId } = useGetTempUserId()
  const router = useRouter()

  // Initialize Auth Web Token
  const { wasAuthWebTokenActive } = useAuthWebToken()

  const [isOpen, setIsOpen] = useState(false)
  const [extensionInstalled, setExtensionInstalled] = useState<
    | {
        installed: boolean
        version: string
      }
    | undefined
  >()
  const [onAuthCallback, setOnAuthCallback] = useState<
    | ((props: {
        authUser: User | null | undefined
        userData: UpdateUserPayload | null | undefined
      }) => void)
    | null
  >(null)
  const [onCloseAuthModal, setOnCloseAuthModal] = useState<(() => void) | null>(
    null
  )
  const [authSource, setAuthSource] = useState<string | null>(null)
  const [modalTitle, setModalTitle] = useState<React.ReactNode | null>(null)
  const [modalSubtitle, setModalSubtitle] = useState<React.ReactNode | null>(
    null
  )
  const [modalAsset, setModalAsset] = useState<string | null>(null)
  const [skipPhoneLink, setSkipPhoneLink] = useState(false)
  const [disableClose, setDisableClose] = useState(false)
  const [fullScreen, setFullScreen] = useState(defaultAuthContext.fullScreen)
  const [blurOverlay, setBlurOverlay] = useState(defaultAuthContext.blurOverlay)
  const [skipOnboarding, setSkipOnboarding] = useState(false)

  const {
    data: userDataResp,
    isLoading: loadingUserData,
    refetch: refetchUser,
  } = useFetchUser()

  const userData = userDataResp?.user

  //  get posthog distinct id
  const posthogId = analytics.getPostHogDistinctId()

  // Dupe Input Store
  const resetDupeInputStore = useDupeInputStore((state) => state.reset)

  // Onboarding Store
  const resetOnboarding = useOnboardingStore((state) => state.resetOnboarding)
  const setOnboardingStepData = useOnboardingStore((state) => state.setStepData)

  const { mutate: maybeRegisterUser } = useMaybeRegister()

  const updateUserDetails = async (props: UpdateUserDetailsProps) => {
    const { subscription, stripeCustomerId, stripeSubscriptionId } = props
    const valuesToStore = {
      metadata: {
        subscription,
        stripeCustomerId,
        stripeSubscriptionId,
      },
    }

    try {
      const token = authUser?.accessToken || (await authUser?.getIdToken?.())
      const res = await axios<{ metadata?: Record<string, any> }>(
        '/api/secure/update-user',
        {
          method: 'POST',
          data: valuesToStore,
          headers: {
            'x-authorization': token,
          },
        }
      )

      if (res?.status !== 200) {
        return {
          status: false,
          userData: {} as UpdateUserPayload,
          error: 'Error updating user details',
        }
      }

      const userData = await refetchUserData()
      return {
        status: true,
        userData: userData?.user || ({} as UpdateUserPayload),
      }
    } catch (error) {
      logger.error('[updateUserDetails]', { error })
      return {
        status: false,
        userData: {} as UpdateUserPayload,
        error: 'Error updating user details',
      }
    }
  }

  const refetchUserData = useCallback(async () => {
    const result = await refetchUser()
    return result.data
  }, [refetchUser])

  /**
   * Opens the auth modal and sets up the callback
   * The callback will be executed either:
   * - When the user completes auth + onboarding
   * - When the user authenticates but closes before completing onboarding
   */
  const openAuthModal = useCallback(
    (props?: OpenModalProps) => {
      // Using authUserNotAnonymous instead of authUser to also ensure that the user is not anonymous
      // and avoid closing the modal if the user is technically authenticated but is anonymous
      if (authUserNotAnonymous && !props?.allowLoggedIn) {
        logger.debug('[user already authenticated] exit')
        setIsOpen(false)
        return
      }

      if (!!props?.source) {
        setAuthSource(props.source)
      }

      if (!!props?.modalTitle) {
        setModalTitle(props.modalTitle)
      }

      if (!!props?.modalSubtitle) {
        setModalSubtitle(props.modalSubtitle)
      }

      if (!!props?.modalAsset) {
        setModalAsset(props.modalAsset)
      }

      if (!!props?.blurOverlay) {
        setBlurOverlay(!!props?.blurOverlay)
      }

      setIsOpen(true)
      setOnAuthCallback(() => props?.callback || null)
      setOnCloseAuthModal(() => props?.onClose || null)

      setSkipPhoneLink(!!props?.skipPhoneLink)
      setDisableClose(!!props?.disableClose)
      setSkipOnboarding(!!props?.skipOnboarding)

      if (props?.fullScreen !== undefined) {
        setFullScreen(!!props?.fullScreen)
      }

      if (props?.blurOverlay !== undefined) {
        setBlurOverlay(!!props?.blurOverlay)
      }

      analytics.track(
        AnalyticsEventName.AuthModalOpened,
        {
          source: props?.source,
          ...(props?.modalTitle && { modalTitle: props.modalTitle }),
          ...(props?.modalSubtitle && { modalSubtitle: props.modalSubtitle }),
          ...(props?.disableClose && { disableClose: props.disableClose }),
          ...(props?.allowLoggedIn && { allowLoggedIn: props.allowLoggedIn }),
        },
        {
          sendToPlausible: true,
          sendToFb: true,
        }
      )
    },
    [analytics, authUserNotAnonymous]
  )

  /**
   * Closes the auth modal
   * If the user is authenticated but hasn't completed onboarding,
   * we still want to execute their callback to maintain the authenticated state
   */
  const closeAuthModal = () => {
    logger.debug('[close triggered] closeAuthModal')
    setIsOpen(false)
    setOnAuthCallback(null)
    setOnCloseAuthModal(null)

    // reset modal state
    // slight delay to avoid ui flicker
    setTimeout(() => {
      setSkipPhoneLink(false)
      setDisableClose(false)
      setFullScreen(true)
      setBlurOverlay(false)
      setSkipOnboarding(false)
      setModalTitle(null)
      setModalSubtitle(null)
      setModalAsset(null)
    }, 300)

    // Clean up URL parameters using Next.js router
    if (router.query.onboarding || router.query.step) {
      const newQuery = { ...router.query }
      delete newQuery.onboarding
      delete newQuery.step

      router.replace(
        {
          pathname:
            router.pathname === '/[hash]'
              ? `/results/${router.pathname}`
              : router.pathname,
          query: newQuery,
        },
        undefined,
        { shallow: true }
      )
    }

    // Only execute onClose callback if Auth Web Token was not active
    if (onCloseAuthModal && !wasAuthWebTokenActive) {
      onCloseAuthModal()
    }
  }

  /**
   * Handles successful authentication and executes the callback
   * This is called in two scenarios:
   * 1. User completes the full auth + onboarding flow
   * 2. User authenticates but closes modal before completing onboarding
   */
  const handleAuthSuccess = (close?: () => void) => {
    // Only execute auth callback if Auth Web Token was not active
    if (onAuthCallback && !wasAuthWebTokenActive) {
      onAuthCallback({ authUser, userData })
      setOnAuthCallback(null)
    }

    close && close()
  }

  const cleanGACookie = (ga: string): string => {
    try {
      return ga.split('.')[2] + '.' + ga.split('.')[3]
    } catch (e) {
      return ga
    }
  }

  const createUserAuthData = async (
    authUser: User | null | undefined
  ): Promise<UserAuthData | undefined> => {
    if (!authUser) return undefined

    const tokenResult = await getUserToken(authUser)

    return {
      uid: authUser.uid,
      accessToken: tokenResult?.token as string,
      tokenResult,
    }
  }

  const postMessageListener = useCallback(
    async (messageType: string, data: Record<string, any>) => {
      setExtensionInstalled(
        (prev) =>
          prev || {
            installed: true,
            ...(data?.version && {
              version: data?.version,
            }),
          }
      )

      switch (messageType) {
        case MessageTypes.AUTH_SYNC: {
          // If we have auth cookie await auth user to be ready
          const authCookie = getCookie(DUPE_AUTH_USER_ID_KEY)
          if (authCookie) {
            await new Promise((resolve) => {
              const unsubscribe = onAuthStateChanged(auth, (user) => {
                if (user) {
                  resolve(true)
                  unsubscribe()
                }
              })
            })
          } else {
            logger.debug('No auth cookie found')
          }

          const gaClientId = cleanGACookie(getCookie('_ga') as string)
          // Continue with auth sync
          const user = auth?.currentUser
          let authData: UserAuthData | undefined
          if (user) {
            authData = await createUserAuthData(user)
          } else {
            logger.debug('No user found to sync')
          }

          // Handle Chrome only to begin with - Later add support for Firefox as does not support `externally_connactable`
          // For Chrome (and Safari) we can message directly to the service worker with sendMessage
          // @ts-ignore
          if (isFirefox) {
            // Support for Firefox
            // send post message
            window.postMessage(
              {
                type: MessageTypes.AUTH_SYNC,
                extensionId: EXTENSION_ID,
                browser: 'firefox',
                userId: user?.uid,
                authData,
                tempUserId,
                gaClientId,
                posthogId,
              },
              '*'
            )
          } else {
            // @ts-ignore
            if (typeof chrome !== 'undefined') {
              // Wrap in try/catch to solve for browser like Arc which are chromium based but not will full chrome feature set.
              try {
                // @ts-ignore
                chrome.runtime.sendMessage(EXTENSION_ID, {
                  type: MessageTypes.AUTH_SYNC,
                  userId: user?.uid,
                  authData,
                  tempUserId,
                  gaClientId,
                  posthogId,
                })
              } catch (e) {
                //noop - likely a browser that is not chrome based such as Arc
                logger.debug('Arc browser error sending message', { error: e })
              }
            } else {
              // Fallback for other browser here
              logger.debug('Fallback for other browser here')
            }
          }
          break
        }
        default:
          break
      }
    },
    [posthogId, tempUserId]
  )

  usePostMessageListener('AuthProvider', postMessageListener)

  // listen for auth state changes
  useEffect(() => {
    if (loadingAuthUser) return
    if (loadingUserData) return

    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user?.uid) {
        // Update auth cookie
        const cookieOptions: OptionsType = {
          expires: new Date(Date.now() + 1000 * MAX_AGE_OF_COOKIE),
          maxAge: MAX_AGE_OF_COOKIE,
          path: '/',
        }

        if (process.env.NEXT_PUBLIC_VERCEL_ENV === 'production') {
          cookieOptions.domain = '.dupe.com'
        }

        setCookie(DUPE_AUTH_USER_ID_KEY, user.uid, cookieOptions)

        // Maybe register user
        if (!userData?.id) {
          maybeRegisterUser()
        }
      } else {
        // clear cookie
        deleteCookie(DUPE_AUTH_USER_ID_KEY, {
          path: '/',
          domain:
            process.env.NEXT_PUBLIC_VERCEL_ENV === 'production'
              ? '.dupe.com'
              : undefined,
        })

        // Reset dupeInputStore when user signs out
        resetDupeInputStore()
        resetOnboarding()
      }
    })

    return () => unsubscribe()
  }, [
    maybeRegisterUser,
    resetDupeInputStore,
    loadingAuthUser,
    loadingUserData,
    userData?.id,
    userData?.walletData?.address,
    resetOnboarding,
  ])

  // call analytics.identify with userData
  useEffect(() => {
    if (!userData) return
    if (!authUser) return

    analytics.identify(userData.id, {
      ...userData,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser, userData])

  // Check URL parameters for onboarding
  useEffect(() => {
    if (!authUser) return
    if (isOpen) return

    const url = new URL(window.location.href)
    const hasOnboarding = url.searchParams.get('onboarding') === '1'
    const status = url.searchParams.get('status')

    let source = `onboarding`

    if (status) {
      source += `:${status}`
    }

    if (hasOnboarding) {
      openAuthModal({
        source,
        allowLoggedIn: true,
      })
    }
  }, [authUser, isOpen, openAuthModal])

  // Update onboarding store with user metadata when both authUser and userData are available
  useEffect(() => {
    if (!authUser || !userData) return
    if (loadingAuthUser || loadingUserData) return

    // Extract subscription data from user metadata
    const subscription = userData.metadata?.subscription || {}
    const stripeCustomerId = userData.metadata?.stripeCustomerId
    const stripeSubscriptionId = userData.metadata?.stripeSubscriptionId
    const onboardingData = userData.metadata?.onboarding_data || {}

    // Update onboarding store with user metadata
    setOnboardingStepData({
      subscription,
      stripeCustomerId,
      stripeSubscriptionId,
      onboarding_data: onboardingData,
    })
  }, [
    authUser,
    userData,
    loadingAuthUser,
    loadingUserData,
    setOnboardingStepData,
  ])

  return (
    <AuthContext.Provider
      value={{
        isOpen,
        openAuthModal,
        closeAuthModal,
        handleAuthSuccess,
        authSource,
        modalTitle,
        modalSubtitle,
        modalAsset,
        userData,
        loadingUserData,
        refetchUserData,
        extensionInstalled,
        skipPhoneLink,
        disableClose,
        fullScreen,
        blurOverlay,
        skipOnboarding,
        updateUserDetails,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export default AuthProvider
